package com.eladfinish.bible.contest.screens.lexicographicalSort

import com.eladfinish.bible.contest.domain.BooksRepository
import com.eladfinish.bible.contest.extensions.runIf
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.OutputChapters
import com.eladfinish.bible.contest.screens.lexicographicalSort.LexicographicalSortLogicUseCase.Companion.DEFAULT_BOOK_NAME_HEBREW
import com.eladfinish.bible.contest.screens.lexicographicalSort.LexicographicalSortLogicUseCase.Companion.DEFAULT_CHAPTER_INDEX
import com.eladfinish.bible.contest.utils.addGershaim
import com.eladfinish.bible.contest.utils.toOtiot

/**
 * Pure logic of the lexicographical sort screen: resolving/validating the selected
 * book and chapter, premium gating of [OutputChapters], and naming the generated PDF.
 *
 * Kept free of any I/O / settings / repositories (other than the static [BooksRepository]
 * data) so it can be unit tested in isolation.
 */
class LexicographicalSortLogicUseCase {

    companion object {
        /** Default book/chapter shown on the very first visit to the screen. */
        private const val DEFAULT_BOOK_NAME_HEBREW = "משלי"
        private const val DEFAULT_CHAPTER_INDEX = 22
    }

    data class BookAndChapter(
        val bookIndex: Int,
        val chapterIndex: Int,
        val numberOfChaptersInBook: Int,
    )

    /**
     * Resolves the initial book/chapter selection for the screen.
     *
     * [requestedBookIndex]/[requestedChapterIndex] are 1-based indices coming from
     * navigation arguments (e.g. the book/chapter the user was browsing); if that pair
     * is valid, it's used as-is.
     *
     * Otherwise, [savedBookIndex]/[savedChapterIndex] (the user's last selection on this
     * screen, persisted across visits) is used, if valid.
     *
     * If neither is valid (e.g. first-ever visit), defaults to [DEFAULT_BOOK_NAME_HEBREW]
     * chapter [DEFAULT_CHAPTER_INDEX] (משלי כ"ב).
     */
    fun initialSelection(
        requestedBookIndex: Int,
        requestedChapterIndex: Int,
        savedBookIndex: Int,
        savedChapterIndex: Int,
    ): BookAndChapter {
        val bookCount = BooksRepository.bookNamesHebrew.size

        val (bookIndex, chapterIndex) = when {
            isValidSelection(requestedBookIndex, requestedChapterIndex, bookCount) ->
                requestedBookIndex to requestedChapterIndex

            savedBookIndex in 1..bookCount ->
                savedBookIndex to savedChapterIndex

            else -> defaultSelection(bookCount)
        }

        val numberOfChapters = BooksRepository.numOfChaptersPerBook[bookIndex - 1]
        val coercedChapterIndex = chapterIndex.coerceIn(1, numberOfChapters)

        return BookAndChapter(
            bookIndex = bookIndex,
            chapterIndex = coercedChapterIndex,
            numberOfChaptersInBook = numberOfChapters,
        )
    }

    /** Whether the navigation arguments name a real book and chapter (they do when the screen is opened from one). */
    fun isRequestedSelectionValid(requestedBookIndex: Int, requestedChapterIndex: Int): Boolean =
        isValidSelection(requestedBookIndex, requestedChapterIndex, BooksRepository.bookNamesHebrew.size)

    private fun isValidSelection(bookIndex: Int, chapterIndex: Int, bookCount: Int): Boolean {
        if (bookIndex !in 1..bookCount) return false
        val numberOfChapters = BooksRepository.numOfChaptersPerBook[bookIndex - 1]
        return chapterIndex in 1..numberOfChapters
    }

    private fun defaultSelection(bookCount: Int): Pair<Int, Int> {
        val defaultBookIndex = BooksRepository.bookNamesHebrew.indexOf(DEFAULT_BOOK_NAME_HEBREW) + 1
        val bookIndex = defaultBookIndex.takeIf { it in 1..bookCount } ?: 1
        return bookIndex to DEFAULT_CHAPTER_INDEX
    }

    /**
     * Resolves the new selection after the user picks a different book.
     * The previously selected chapter is coerced into the new book's chapter range.
     * Returns `null` if [bookIndex] is out of range (selection should not change).
     */
    fun selectBook(bookIndex: Int, currentChapterIndex: Int): BookAndChapter? {
        val bookCount = BooksRepository.bookNamesHebrew.size
        if (bookIndex !in 1..bookCount) return null

        val numberOfChapters = BooksRepository.numOfChaptersPerBook[bookIndex - 1]
        val chapterIndex = currentChapterIndex.coerceIn(1, numberOfChapters)

        return BookAndChapter(
            bookIndex = bookIndex,
            chapterIndex = chapterIndex,
            numberOfChaptersInBook = numberOfChapters,
        )
    }

    /**
     * Returns whether [chapterIndex] is a valid chapter selection given that the
     * currently selected book has [numberOfChaptersInSelectedBook] chapters.
     */
    fun isChapterSelectionValid(chapterIndex: Int, numberOfChaptersInSelectedBook: Int): Boolean {
        return chapterIndex in 1..numberOfChaptersInSelectedBook
    }

    /**
     * Returns `true` if selecting [outputChapters] requires a premium subscription
     * that the user doesn't currently have (i.e. the "premium required" dialog
     * should be shown instead of proceeding).
     */
    fun requiresPremiumUpsell(outputChapters: OutputChapters, isPremium: Boolean): Boolean {
        return outputChapters.isPremium && !isPremium
    }

    /**
     * Builds the (Hebrew) file name for the generated PDF, e.g.
     * "בראשית א סדר לקסיקוגרפי", "שמות לפי מיקוד סדר לקסיקוגרפי" or
     * "ויקרא סדר לקסיקוגרפי" (for [OutputChapters.ALL]).
     */
    fun buildPdfName(
        bookIndex: Int,
        chapterIndex: Int,
        outputChapters: OutputChapters,
        withGershaim: Boolean = false,
    ): String {
        val hebrewBookName = BooksRepository.bookNamesHebrew[bookIndex - 1]
        var pdfName = "$hebrewBookName "
        pdfName += when (outputChapters) {
            OutputChapters.SINGLE -> "${chapterIndex.toOtiot().runIf(condition = withGershaim) { addGershaim() }} "
            OutputChapters.BY_MIKUD -> "לפי מיקוד "
            OutputChapters.ALL -> ""
        }
        pdfName += "סדר לקסיקוגרפי"
        return pdfName
    }

    /**
     * Builds the screen-title-style name used for the emailed PDF's file name, e.g.
     * "מיון לקסיקוגרפי - בראשית פרק א'", "מיון לקסיקוגרפי - שמות (לפי מיקוד)" or
     * "מיון לקסיקוגרפי - ויקרא (כל הספר)" (for [OutputChapters.ALL]).
     */
    fun buildPdfTitle(bookIndex: Int, chapterIndex: Int, outputChapters: OutputChapters): String {
        val bookName = BooksRepository.bookNamesHebrew.getOrNull(bookIndex - 1) ?: ""
        val suffix = when (outputChapters) {
            OutputChapters.SINGLE -> " פרק ${chapterIndex.toOtiot().addGershaim()}"
            OutputChapters.BY_MIKUD -> " (לפי מיקוד)"
            OutputChapters.ALL -> " (כל הספר)"
        }
        return "מיון לקסיקוגרפי - $bookName$suffix"
    }
}
